import type { TowerDesign } from "@/game/towerSpec";
import type { Material } from "@/physics/model";
import type { MountedLoad } from "@/game/appurtenances";
import { CATALOG } from "@/game/appurtenances";

/**
 * Southbury 226 ft monopole — a real Engineered Endeavors (EEI) pole in New Haven
 * County CT (Crown Castle BU 806358), rebuilt from the stamped tnxTower structural
 * analysis (Rev 4, 2022-04-25) in reference_towers/02_engineering_drawings/
 * CT_Southbury_226ft_monopole_structural.pdf.
 *
 * Real structure (Table 4 section capacity + E-1 elevation):
 *   - 18-sided tapered tube, 6 sections over 225.79 ft (68.82 m).
 *   - Base OD 74.50" (1.892 m), 0.5625" wall; tip OD 21.50" (0.546 m), 0.1875".
 *   - Grade A572-65 (Fy 65 ksi / 448 MPa, Fu 80 ksi) — inline below.
 *   - Pole steel weight ≈ 65.3 kip ≈ 29.6 t.
 *
 * Design basis (p.3, E-1): TIA-222-H, Risk Cat II, 116 mph ultimate 3-sec gust,
 * Exposure B, Topo 1; also 50 mph + 1.00" ice; service 60 mph. Factored base
 * reactions (116 mph): axial 114 kip (507 kN), shear 45 kip (200 kN), overturning
 * moment 7446 kip-ft (10.1 MN·m). Structure rating 62.2%.
 *
 * Foundation & connection (Table 5): anchor rods 57.8%, base plate 43.4%, and the
 * base FOUNDATION governs at 62.2% (vs the original design reactions). Modelled as
 * a large drilled pier on dense glacial till with a competent anchor-bolt base
 * plate (not governing, matching the 57.8% rod rating).
 *
 * Fat pole → the slim-lattice + `shell` stand-in is geometrically faithful: the
 * internal 12-leg cage at 1.9 m across ≈ the real 1.892 m base OD; faces 12 rounds
 * the real 18-sided section. Appurtenances: a very heavy multi-carrier stack
 * (Tables 1-2: arrays at 207/187/173 ft and more) across three azimuths.
 */

const A572_65: Material = { name: "A572 Gr65", E: 200e9, Fy: 448e6, rho: 7850 };
const AZ = [0, (2 * Math.PI) / 3, (4 * Math.PI) / 3];

const arrays: MountedLoad[] = [
  // 207 ft — top array: CommScope/Ericsson/RFS panels + radios
  ...AZ.flatMap((azimuth) => [
    { appurtenance: CATALOG.panelSector, height: 63.1, azimuth },
    { appurtenance: CATALOG.panelSector, height: 63.1, azimuth },
    { appurtenance: CATALOG.rru, height: 63.1, azimuth },
    { appurtenance: CATALOG.rru, height: 63.1, azimuth },
  ]),
  // 187 ft — CCI/Ericsson/Kathrein array (very dense in the report)
  ...AZ.flatMap((azimuth) => [
    { appurtenance: CATALOG.panelSector, height: 57.0, azimuth },
    { appurtenance: CATALOG.panelSector, height: 57.0, azimuth },
    { appurtenance: CATALOG.rru, height: 57.0, azimuth },
  ]),
  // 173 ft — Alcatel/RFS array
  ...AZ.flatMap((azimuth) => [
    { appurtenance: CATALOG.panelSector, height: 52.7, azimuth },
    { appurtenance: CATALOG.rru, height: 52.7, azimuth },
  ]),
  // feed-line bundles, base → top array
  { appurtenance: CATALOG.coaxBundle, height: 63.1, runLength: 63.1 },
  { appurtenance: CATALOG.coaxBundle, height: 63.1, runLength: 63.1 },
];

export const design: TowerDesign = {
  id: "monopole-southbury-226ft",
  name: "Monopole (Southbury 226 ft, EEI)",
  description: "Real 226 ft EEI A572-65 monopole, Southbury CT — foundation-critical (Crown Castle BU 806358).",
  author: "reference: CT Southbury 226 ft tnxTower SA Rev 4",
  spec: {
    type: "monopole",
    height: 68.82, // 225.79 ft
    stack: {
      faces: 12, // rounds the real 18-sided pole
      panels: 20, // 3.44 m panels
      baseRadius: 0.95, // 1.9 m across ≈ real 1.892 m base OD
      topRadius: 0.28, // 0.56 m ≈ real 0.546 m tip
      legSection: "legXXL",
      braceSection: "braceHeavy",
      legMaterial: A572_65,
      braceMaterial: A572_65,
      bracing: "X",
    },
    shell: {
      baseDiameter: 1.892, // real EEI base OD, 18-sided
      topDiameter: 0.546,
    },
    foundation: {
      soil: "dense-sand", // dense New Haven County glacial till — foundation governs at 62.2%
      base: { type: "pier", width: 3.2, diameter: 3.2, depth: 9.0, concrete: "c30" },
      connection: {
        kind: "anchorBolts",
        nBolts: 20,
        bolt: "b2_0",
        grade: "f1554_105",
        boltCircleDia: 2.0,
        embedment: 0.55,
        edgeDistance: 0.35,
        platePlanArea: 3.1,
        plateThickness: 0.07,
        plateGrade: "a36rod",
        fc: 30e6,
      },
    },
    loads: arrays,
  },
};
